import {
  isModuleAvailable,
  modulesForWorkspace,
  ORGANIZATION_ROLE,
  WORKSPACE_MODULE,
  WORKSPACE_TYPE,
  type OrganizationRole,
  type WorkspaceModule,
  type WorkspaceType,
} from '@29100/shared';
import type { MessageKey } from '../i18n';

/**
 * Navegación de la aplicación autenticada: a dónde entra cada uno y qué
 * secciones ve una vez dentro.
 *
 * Todo esto son funciones puras con tests porque los fallos que arreglan son
 * silenciosos —la aplicación funciona, simplemente enseña la pantalla
 * equivocada— y ese tipo de fallo vuelve solo si nadie lo fija.
 */

/** Primera pantalla dentro de la aplicación. */
export const HOME = '/dashboard';

/**
 * Rutas que nunca pueden ser destino tras autenticarse.
 *
 * `/login` y `/register` provocarían un rebote infinito; la landing y los
 * precios son públicos y devolverían al usuario fuera de la aplicación justo
 * después de entrar, que es exactamente la confusión que se quiere evitar.
 */
const NO_APTAS = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/pricing',
]);

/**
 * Destino tras autenticarse.
 *
 * El fallo que motivó esta función: iniciar sesión devolvía a `/`, que dejó de
 * ser el dashboard el día que se añadió la landing, así que el usuario acababa
 * en la página de marketing con su sesión ya iniciada, buscando un botón para
 * entrar en su propia cuenta.
 *
 * @param from Ruta que el visitante intentaba abrir antes de que la puerta le
 *   mandara al login. Puede venir vacía, ser una URL absoluta de otro sitio, o
 *   ser una de las rutas no aptas.
 */
export function resolveAfterLogin(from: unknown): string {
  if (typeof from !== 'string' || from.length === 0) return HOME;

  // Sólo rutas internas: `//evil.com` y `https://evil.com` son redirecciones
  // abiertas —el atacante manda a la víctima a su sitio a través del nuestro—
  // y una barra inicial no basta para descartarlas.
  if (!from.startsWith('/') || from.startsWith('//')) return HOME;

  /*
   * La barra invertida, que es la misma redirección abierta disfrazada.
   *
   * `/\\evil.com` pasa las dos comprobaciones de arriba —empieza por `/` y no
   * por `//`— pero varios navegadores normalizan `\\` a `/` al resolver la URL,
   * así que acaba siendo `//evil.com`. Es el aviso GHSA-wrjc-x8rr-h8h6 de React
   * Router, que en la rama 6 no tiene versión corregida.
   *
   * El camino completo es real: basta con que la víctima abra
   * `29100cloud.com/\\evil.com` sin sesión. La guarda de `App.tsx` guarda
   * `location.pathname` tal cual en el estado de navegación, y al entrar se la
   * devuelve. Sale del sitio justo después de escribir la contraseña, y con la
   * confianza de venir de aquí.
   *
   * Se descarta cualquier barra invertida, no sólo la del principio: no hay
   * ninguna ruta legítima de esta aplicación que lleve una, así que no hay nada
   * que distinguir. Y va aquí, en nuestro código, para no depender de la
   * versión del router que haya debajo.
   */
  if (from.includes('\\')) return HOME;

  const ruta = from.split(/[?#]/)[0] ?? '/';
  return NO_APTAS.has(ruta) ? HOME : from;
}

/**
 * Ancho de la columna en las pantallas de acceso.
 *
 * Entrar, recuperar la contraseña o verificar el correo son formularios de
 * cuatro campos: una columna estrecha se lee mejor. El alta, en cambio, tiene
 * que enseñar el catálogo de perfiles —veintiún oficios en la cuenta de
 * empresa—, y en 28 rem esa rejilla no cabe en más de una columna: se convierte
 * en una lista de veintiún bloques que hay que recorrer entera.
 *
 * La decisión vive aquí, junto al resto de las que deciden qué se enseña, y no
 * dentro del layout: es una función pura y se puede fijar con un test, que es
 * lo que hace falta con un fallo que no rompe nada y sólo se ve mirando.
 */
export function authColumnClass(pathname: string): string {
  return pathname === '/register' ? 'max-w-3xl' : 'max-w-md';
}

/* ------------------------------------------------------------------------ */
/* Opciones de cada espacio                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Grupos del menú lateral.
 *
 * Dieciséis secciones seguidas no se leen: se recorren. Agrupadas por la
 * pregunta que responden —«¿qué he compartido?», «¿qué me sobra?»— el menú deja
 * de ser una lista y pasa a ser un índice, y el usuario puede plegar lo que no
 * usa sin perderlo.
 *
 * El grupo es del **menú**, no del producto: qué existe en cada espacio lo
 * sigue decidiendo `WORKSPACE_MODULE_SCOPE`. Un grupo cuyas secciones no
 * existan en el espacio activo desaparece entero, sin dejar una cabecera vacía.
 */
export const NAV_GROUP = {
  /** Lo que se abre a diario. Va suelto, arriba y sin cabecera. */
  MAIN: 'MAIN',
  LIBRARY: 'LIBRARY',
  SHARING: 'SHARING',
  ORGANIZATION: 'ORGANIZATION',
  SPACE: 'SPACE',
  /** Ajustes. Va suelto, al final, donde se busca. */
  ACCOUNT: 'ACCOUNT',
} as const;
export type NavGroup = (typeof NAV_GROUP)[keyof typeof NAV_GROUP];

/**
 * Qué secciones ofrece cada espacio.
 *
 * Vive aquí, y no dentro del layout, porque no es sólo un menú: también decide
 * si la pantalla abierta sigue teniendo sentido después de cambiar de espacio.
 * Con las listas en un sitio y la comprobación en otro se contradicen en cuanto
 * alguien toca una de las dos.
 *
 * **A qué producto pertenece cada sección no se decide aquí**, sino en
 * `WORKSPACE_MODULE_SCOPE` (`@29100/shared`), que es el mismo catálogo que
 * consulta la API antes de atender. Aquí sólo se le pone ruta, etiqueta e
 * icono: dos listas escritas a mano eran justamente lo que permitió que nueve
 * de las doce secciones del espacio personal fueran idénticas a las de empresa
 * sin que nadie se diera cuenta.
 */
export interface NavItem {
  to: string;
  /** Sección del catálogo compartido. Decide en qué espacios existe. */
  module: WorkspaceModule;
  /** Clave del catálogo: el texto se resuelve al pintar, no al declarar. */
  label: MessageKey;
  /**
   * El nombre corto, para la barra inferior del móvil.
   *
   * Ahí hay cinco columnas en 390 px —78 px cada una— y «Enlaces compartidos»
   * no cabe: se partía en dos líneas y la segunda se salía de la barra por
   * abajo, encima del contenido. El menú lateral tiene sitio de sobra y se
   * queda con el nombre largo, que es el que distingue «Carpetas compartidas»
   * de «Enlaces compartidos».
   *
   * Sin valor, la barra usa `label`: sólo lo llevan los nombres que no caben.
   */
  corto?: MessageKey;
  icon: string;
  /** Bajo qué cabecera del menú se pinta. */
  group: NavGroup;
  /**
   * Una línea de qué hay dentro.
   *
   * No se usa en el menú —ahí sobra— sino en la ventana de elección del grupo,
   * donde la diferencia entre «Carpetas compartidas» y «Enlaces compartidos»
   * no es evidente sólo por el nombre.
   */
  description?: MessageKey;
  end?: boolean;
  /**
   * Roles que pueden abrirla. Sin valor, la ve todo el mundo.
   *
   * No es una comprobación de seguridad —el servidor la hace de todos modos, y
   * es la única que cuenta— sino de honestidad: enseñar una puerta que devuelve
   * un 403 al empujarla es peor que no enseñarla.
   */
  roles?: OrganizationRole[];
  /** Espacios en cuya barra inferior de móvil aparece. Ausente: en ninguna. */
  movil?: EnMovil;
}

/** Tipo de espacio activo, tal y como lo distingue el producto. */
export type WorkspaceKind = 'personal' | 'organization';

const MANDA: OrganizationRole[] = [ORGANIZATION_ROLE.OWNER, ORGANIZATION_ROLE.ADMIN];

/**
 * En qué barras inferiores aparece la sección.
 *
 * La barra de móvil no es el menú recortado: es lo crítico con el pulgar
 * —subir, ver, compartir—, y cada espacio tiene lo suyo. Ausente significa que
 * no sale en móvil, que es el caso de la mayoría.
 */
type EnMovil = 'both' | WorkspaceKind;

/**
 * Todas las secciones de la aplicación autenticada, una sola vez.
 *
 * El orden es el del menú lateral. `movil` marca las cinco de cada barra
 * inferior: son exactamente cinco porque la barra reparte el ancho en cinco
 * columnas y una sexta las estrecha hasta que el texto no cabe —lo comprueba
 * un test, porque añadir una sección aquí es justo cuando se rompe—.
 */
const SECCIONES: NavItem[] = [
  {
    to: '/dashboard',
    module: WORKSPACE_MODULE.DASHBOARD,
    label: 'nav.dashboard',
    icon: '◇',
    group: NAV_GROUP.MAIN,
    end: true,
    movil: 'both',
  },
  {
    to: '/files',
    module: WORKSPACE_MODULE.FILES,
    label: 'nav.files',
    corto: 'nav.filesShort',
    icon: '🗂️',
    group: NAV_GROUP.MAIN,
    movil: 'both',
  },

  {
    to: '/photos',
    module: WORKSPACE_MODULE.PHOTOS,
    label: 'nav.photos',
    icon: '🖼️',
    group: NAV_GROUP.LIBRARY,
    movil: 'personal',
  },
  {
    to: '/albums',
    module: WORKSPACE_MODULE.ALBUMS,
    label: 'nav.albums',
    icon: '📔',
    group: NAV_GROUP.LIBRARY,
  },
  {
    to: '/favorites',
    module: WORKSPACE_MODULE.FAVORITES,
    label: 'nav.favorites',
    icon: '★',
    group: NAV_GROUP.LIBRARY,
  },
  {
    to: '/checklists',
    module: WORKSPACE_MODULE.CHECKLISTS,
    label: 'nav.checklists',
    icon: '☑',
    group: NAV_GROUP.LIBRARY,
  },

  {
    to: '/business/shared-folders',
    module: WORKSPACE_MODULE.SHARED_FOLDERS,
    label: 'nav.sharedFolders',
    corto: 'nav.sharedFoldersShort',
    icon: '📂',
    group: NAV_GROUP.SHARING,
    description: 'navHint.sharedFolders',
    movil: 'organization',
  },
  {
    to: '/shared-links',
    module: WORKSPACE_MODULE.SHARED_LINKS,
    label: 'nav.sharedLinks',
    corto: 'nav.sharedLinksShort',
    icon: '🔗',
    group: NAV_GROUP.SHARING,
    description: 'navHint.sharedLinks',
    movil: 'personal',
  },
  {
    to: '/business/requests',
    module: WORKSPACE_MODULE.DOCUMENT_REQUESTS,
    label: 'nav.requests',
    icon: '📥',
    group: NAV_GROUP.SHARING,
    description: 'navHint.requests',
  },

  {
    to: '/business/team',
    module: WORKSPACE_MODULE.TEAM,
    label: 'nav.team',
    icon: '👥',
    group: NAV_GROUP.ORGANIZATION,
    movil: 'organization',
  },
  {
    to: '/reviews',
    module: WORKSPACE_MODULE.REVIEWS,
    label: 'nav.reviews',
    icon: '📅',
    group: NAV_GROUP.ORGANIZATION,
  },
  {
    to: '/activity',
    module: WORKSPACE_MODULE.ACTIVITY,
    label: 'nav.activity',
    icon: '≡',
    group: NAV_GROUP.ORGANIZATION,
  },
  {
    to: '/business/reports',
    module: WORKSPACE_MODULE.REPORTS,
    label: 'nav.reports',
    icon: '📈',
    group: NAV_GROUP.ORGANIZATION,
    roles: MANDA,
  },

  {
    to: '/cleanup',
    module: WORKSPACE_MODULE.CLEANUP,
    label: 'nav.cleanup',
    icon: '🧹',
    group: NAV_GROUP.SPACE,
    description: 'navHint.cleanup',
  },
  {
    to: '/trash',
    module: WORKSPACE_MODULE.TRASH,
    label: 'nav.trash',
    icon: '🗑️',
    group: NAV_GROUP.SPACE,
    description: 'navHint.trash',
  },

  {
    to: '/settings',
    module: WORKSPACE_MODULE.SETTINGS,
    label: 'nav.settings',
    icon: '⚙️',
    group: NAV_GROUP.ACCOUNT,
    movil: 'both',
  },
];

/**
 * Cabecera de cada grupo, en el orden en que se pintan.
 *
 * `label` a `null` significa que el grupo va suelto, sin cabecera ni plegado:
 * poner «Principal» encima de «Inicio» y «Mis archivos» es una etiqueta que no
 * dice nada, y «Ajustes» se busca al final, no dentro de una carpeta.
 *
 * `hub` es la ventana de elección del grupo —la pantalla que enseña sus
 * secciones como tarjetas—. Sólo la tienen los grupos donde la duda es real:
 * «Compartido» separa lo que yo he abierto de lo que me han abierto, y
 * «Espacio» separa proponer un borrado de deshacerlo. En un grupo con una sola
 * sección disponible la ventana sobra, y `groupedNavigationFor` la retira.
 */
interface GrupoDef {
  id: NavGroup;
  label: MessageKey | null;
  description: MessageKey | null;
  icon: string;
  hub: string | null;
}

const GRUPOS: GrupoDef[] = [
  { id: NAV_GROUP.MAIN, label: null, description: null, icon: '', hub: null },
  {
    id: NAV_GROUP.LIBRARY,
    label: 'nav.group.library',
    description: 'nav.group.libraryBody',
    icon: '📚',
    hub: null,
  },
  {
    id: NAV_GROUP.SHARING,
    label: 'nav.group.sharing',
    description: 'nav.group.sharingBody',
    icon: '🤝',
    hub: '/shared',
  },
  {
    id: NAV_GROUP.ORGANIZATION,
    label: 'nav.group.organization',
    description: 'nav.group.organizationBody',
    icon: '🏢',
    hub: null,
  },
  {
    id: NAV_GROUP.SPACE,
    label: 'nav.group.space',
    description: 'nav.group.spaceBody',
    icon: '🧹',
    hub: '/space',
  },
  { id: NAV_GROUP.ACCOUNT, label: null, description: null, icon: '', hub: null },
];

/** Un grupo del menú con las secciones que el espacio activo sí ofrece. */
export interface NavSection extends GrupoDef {
  items: NavItem[];
}

function tipoDeEspacio(kind: WorkspaceKind): WorkspaceType {
  return kind === 'organization' ? WORKSPACE_TYPE.ORGANIZATION : WORKSPACE_TYPE.PERSONAL;
}

function delEspacio(kind: WorkspaceKind): Set<WorkspaceModule> {
  return new Set(modulesForWorkspace(tipoDeEspacio(kind)));
}

function paraElRol(items: NavItem[], role: OrganizationRole | string | null): NavItem[] {
  return items.filter((item) => !item.roles || item.roles.some((permitido) => permitido === role));
}

/** Secciones de la barra lateral en el espacio activo. */
export function navigationFor(
  kind: WorkspaceKind,
  role: OrganizationRole | string | null,
): NavItem[] {
  const ofrecidos = delEspacio(kind);
  return paraElRol(
    SECCIONES.filter((item) => ofrecidos.has(item.module)),
    role,
  );
}

/** Secciones de la barra inferior en el espacio activo. */
export function mobileNavigationFor(
  kind: WorkspaceKind,
  role: OrganizationRole | string | null,
): NavItem[] {
  return navigationFor(kind, role).filter((item) => item.movil === 'both' || item.movil === kind);
}

/**
 * Las mismas secciones, repartidas en los grupos del menú.
 *
 * Es `navigationFor` visto por grupos, no otra lista: parte de ella para que
 * no puedan discrepar. Un grupo sin secciones disponibles no se devuelve —una
 * cabecera «Organización» vacía en el espacio personal sería peor que no
 * agrupar—, y la ventana de elección se retira cuando sólo queda una sección:
 * una pantalla para elegir entre una única opción es un clic de más.
 */
export function groupedNavigationFor(
  kind: WorkspaceKind,
  role: OrganizationRole | string | null,
): NavSection[] {
  const disponibles = navigationFor(kind, role);

  return GRUPOS.map((grupo) => {
    const items = disponibles.filter((item) => item.group === grupo.id);
    return { ...grupo, hub: items.length > 1 ? grupo.hub : null, items };
  }).filter((grupo) => grupo.items.length > 0);
}

/**
 * El grupo cuya ventana de elección es esta ruta.
 *
 * Lo usan las propias ventanas: la lista de tarjetas es la del grupo, filtrada
 * por el espacio activo, así que nadie tiene que repetirla. Devuelve `null` si
 * la ruta no es una ventana o si en este espacio el grupo se quedó sin nada
 * —la pantalla existe, pero no tiene nada que ofrecer—.
 */
export function hubSectionFor(
  pathname: string,
  kind: WorkspaceKind,
  role: OrganizationRole | string | null,
): NavSection | null {
  const ruta = pathname.split(/[?#]/)[0] ?? pathname;
  const grupo = GRUPOS.find((candidato) => candidato.hub === ruta);
  if (!grupo) return null;

  const items = navigationFor(kind, role).filter((item) => item.group === grupo.id);
  return items.length > 0 ? { ...grupo, items } : null;
}

/**
 * A qué grupo pertenece la pantalla abierta.
 *
 * El menú lo necesita para abrir el grupo que contiene la pantalla en la que
 * está el usuario: quien plegó «Espacio» y llega a la papelera desde un enlace
 * vería el menú sin ninguna marca de dónde está.
 */
export function groupForPath(pathname: string): NavGroup | null {
  const ruta = pathname.split(/[?#]/)[0] ?? pathname;

  const ventana = GRUPOS.find((grupo) => grupo.hub === ruta);
  if (ventana) return ventana.id;

  const modulo = moduleForPath(ruta);
  return SECCIONES.find((item) => item.module === modulo)?.group ?? null;
}

/**
 * Dónde dejar al usuario después de cambiar de espacio.
 *
 * Cambiar de empresa a personal estando en «Equipo» dejaba una pantalla que en
 * el espacio personal no significa nada. Devuelve la ruta a la que hay que
 * mover al usuario, o `null` si la que tiene abierta sigue siendo válida —así
 * quien está en «Mis archivos» se queda donde estaba, viendo los archivos del
 * nuevo espacio, que es justo lo que esperaba al cambiar.
 *
 * Antes miraba si la ruta empezaba por `/business/`, y ese atajo dejó fuera
 * las dos secciones de empresa que no viven bajo ese prefijo: «Revisiones»
 * (`/reviews`) y «Actividad» (`/activity`). Cambiar a personal desde
 * cualquiera de las dos te dejaba dentro de una pantalla que en ese espacio no
 * existe, pidiendo a la API datos que ahora rechaza. Ahora se pregunta al
 * catálogo, que es quien lo sabe.
 *
 * Sólo saca de las pantallas de empresa: las personales (fotos, álbumes,
 * favoritos) funcionan igual en un espacio de organización aunque el menú no
 * las ofrezca, y echar de una página que funciona sería quitar algo que hoy se
 * usa.
 */
export function resolveWorkspaceRoute(pathname: string, kind: WorkspaceKind): string | null {
  const modulo = moduleForPath(pathname);
  if (!modulo) return null;
  return isModuleAvailable(modulo, tipoDeEspacio(kind)) ? null : HOME;
}

/**
 * A qué sección pertenece una ruta abierta.
 *
 * Compara por prefijo para que las pantallas de detalle —`/albums/:id`,
 * `/business/requests/:id`— cuenten como su sección: sin eso, entrar al
 * detalle de una solicitud desde un espacio personal se colaría por no ser
 * exactamente la ruta del menú.
 */
export function moduleForPath(pathname: string): WorkspaceModule | null {
  const ruta = pathname.split(/[?#]/)[0] ?? pathname;

  return (
    POR_ESPECIFICIDAD.find((item) => ruta === item.to || ruta.startsWith(`${item.to}/`))?.module ??
    null
  );
}

/**
 * De la más específica a la más general.
 *
 * `/business/requests/:id` tiene que resolverse como solicitudes y no quedarse
 * en el primer prefijo que encaje. Se ordena una vez, al cargar el módulo: esto
 * se pregunta en cada repintado del armazón.
 */
const POR_ESPECIFICIDAD = [...SECCIONES].sort((a, b) => b.to.length - a.to.length);
